// The one rule for whether a verification record counts as verified: it passed, and it has no
// expiry or has not expired yet. The expiry is the server's `credentialExpiresAt` whenever the
// record carries that key (null meaning it never expires); an older record without it falls back
// to its stored `expiryDate`, read as valid through the end of that UTC day. An expiry that cannot
// be read counts as already passed. Passed means approved, or completed (where a check with no
// approval step rests) with every required check confirmed passed: the server reads that from the
// provider and records it as `requiredChecksPassed`, so a completed record without it does not
// count. Every verified badge, filter and sort uses these, in the browser and on the API server
// alike.
//
// Written as plain CommonJS with no imports so the API server, which runs its files under plain
// node, can require this same file the browser bundle imports.

const hasPassed = record =>
  record.status === 'approved' || (record.status === 'completed' && record.requiredChecksPassed === true);

const toTime = value => (value instanceof Date ? value.getTime() : Date.parse(value));

// The moment a record expires, in milliseconds, or null when it never does.
const expiryOf = record => {
  if (record.credentialExpiresAt !== undefined) {
    if (record.credentialExpiresAt === null) return null;
    const time = toTime(record.credentialExpiresAt);
    return Number.isNaN(time) ? 0 : time;
  }
  if (!record.expiryDate) return null;
  const time = toTime(record.expiryDate);
  if (Number.isNaN(time)) return 0;
  const date = new Date(time);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999);
};

const isRecordExpired = (record, now = Date.now()) => {
  const expiry = expiryOf(record);
  return expiry !== null && now > expiry;
};

const isVerifiedRecord = (record, now = Date.now()) =>
  !!record && hasPassed(record) && !isRecordExpired(record, now);

const hasVerifiedCategory = (records, prefix, now = Date.now()) =>
  Array.isArray(records) &&
  records.some(
    record =>
      typeof record?.documentType === 'string' &&
      record.documentType.startsWith(prefix) &&
      isVerifiedRecord(record, now)
  );

const hasVerifiedType = (records, documentType, now = Date.now()) =>
  Array.isArray(records) &&
  records.some(record => record?.documentType === documentType && isVerifiedRecord(record, now));

// A company's only evidence today is its business verification.
const isCompanyVerified = (records, now = Date.now()) => hasVerifiedType(records, 'kyb-1', now);

module.exports = {
  expiryOf,
  isRecordExpired,
  isVerifiedRecord,
  hasVerifiedCategory,
  hasVerifiedType,
  isCompanyVerified,
};
